// Interface for the standardized JSON format
export interface ClassificationData {
  topic?: string;
  subtopic?: string;
  categories?: string[];
}

export interface LocationData {
  query: string;
  city_or_town?: string;
  state_or_province?: string;
  country?: string;
  present: 'Y' | 'N';
  confidence: number;
}

export interface IntentData {
  commercial_score: number;
  specificity_score: number;
  search_intent: 'informational' | 'navigational' | 'transactional' | 'commercial';
}

export interface JSONRecord {
  id: string;
  query: string;
  timestamp: string;
  classification: {
    topics: Array<{
      name: string;
      field: string;
      confidence: number;
      source_module?: string;
      type?: string;
    }>;
    subtopics: Array<{
      name: string;
      field: string;
      confidence: number;
      source_module?: string;
      type?: string;
      parent_topic?: string;
    }>;
    scores: Record<string, number>;
    location: Record<string, any>;
    metadata: Record<string, string>;
    hierarchy?: Record<string, any>;
  };
  validation: {
    is_valid: boolean;
    errors: string | null;
  };
}

export interface ProcessedData {
  records: JSONRecord[];
  categoryData: CategoryData;
  locationStats: LocationStats;
  intentStats: IntentStats;
  sourceStats: SourceStats;
}

export interface CategoryData {
  categories: string[];
  categoryCounts: Record<string, number>;
  wordFrequencies: { name: string; value: number }[];
  multiCategoryCount: number[];
  coOccurrenceMatrix: number[][];
  coOccurrenceData: { name: string; value: number }[];
  categoryMapping: Record<string, string>;
}

export interface LocationStats {
  totalWithLocation: number;
  percentageWithLocation: number;
  averageConfidence: number;
  cityDistribution: Record<string, number>;
  stateDistribution: Record<string, number>;
}

export interface IntentStats {
  averageCommercialScore: number;
  averageSpecificityScore: number;
  intentDistribution: Record<string, number>;
}

export interface SourceStats {
  topicSourceDistribution: Record<string, number>;
  subtopicSourceDistribution: Record<string, number>;
}

export function parseJSONData(jsonData: string | JSONRecord[]): ProcessedData {
  // Parse JSON if it's a string
  let records: JSONRecord[];
  if (typeof jsonData === 'string') {
    try {
      records = JSON.parse(jsonData);
    } catch (error) {
      console.error('Failed to parse JSON data:', error);
      throw new Error('Invalid JSON format');
    }
  } else {
    records = jsonData;
  }

  // Validate that we have an array
  if (!Array.isArray(records)) {
    console.error('JSON data is not an array:', records);
    throw new Error('Expected an array of records');
  }

  // Process category data
  const categoryData = processCategoryData(records);
  const locationStats = processLocationStats(records);
  const intentStats = processIntentStats(records);
  const sourceStats = processSourceStats(records);

  return {
    records,
    categoryData,
    locationStats,
    intentStats,
    sourceStats
  };
}

function processCategoryData(records: JSONRecord[]): CategoryData {
  // Collect all unique categories (topics + subtopics + categories)
  const allCategories = new Set<string>();
  const categoryCounts: Record<string, number> = {};
  const wordFreq: Record<string, number> = {};
  
  // Track which categories appear together
  const categoryAssignments: string[][] = [];

  records.forEach(record => {
    const recordCategories: string[] = [];
    
    // Add topics
    record.classification.topics.forEach(topic => {
      allCategories.add(topic.name);
      recordCategories.push(topic.name);
      categoryCounts[topic.name] = (categoryCounts[topic.name] || 0) + 1;
    });
    
    // Add subtopics
    record.classification.subtopics.forEach(subtopic => {
      allCategories.add(subtopic.name);
      recordCategories.push(subtopic.name);
      categoryCounts[subtopic.name] = (categoryCounts[subtopic.name] || 0) + 1;
    });
    
    // Process keywords for word cloud
    if (record.query) {
      const words = record.query.toLowerCase().split(/\s+/);
      words.forEach(word => {
        word = word.replace(/[^a-z0-9]/g, '');
        if (word.length > 2) {
          wordFreq[word] = (wordFreq[word] || 0) + 1;
        }
      });
    }
    
    categoryAssignments.push(recordCategories);
  });

  const categories = Array.from(allCategories).sort();
  const categoryCount = categories.length;
  
  // Build co-occurrence matrix
  const coOccurrenceMatrix = Array(categoryCount).fill(0).map(() => Array(categoryCount).fill(0));
  const categoryIndex: Record<string, number> = {};
  categories.forEach((cat, idx) => {
    categoryIndex[cat] = idx;
  });

  // Calculate co-occurrences
  categoryAssignments.forEach(assignment => {
    for (let i = 0; i < assignment.length; i++) {
      for (let j = 0; j < assignment.length; j++) {
        if (i !== j) {
          const idx1 = categoryIndex[assignment[i]];
          const idx2 = categoryIndex[assignment[j]];
          if (idx1 !== undefined && idx2 !== undefined) {
            coOccurrenceMatrix[idx1][idx2]++;
          }
        }
      }
    }
  });

  // Build co-occurrence data for visualization
  const coOccurrenceData: { name: string; value: number }[] = [];
  for (let i = 0; i < categories.length; i++) {
    for (let j = 0; j < categories.length; j++) {
      if (i !== j && coOccurrenceMatrix[i][j] > 0) {
        coOccurrenceData.push({
          name: `${categories[i]} → ${categories[j]}`,
          value: coOccurrenceMatrix[i][j]
        });
      }
    }
  }

  // Count multi-category assignments
  const multiCategoryCount = Array(categoryCount + 1).fill(0);
  categoryAssignments.forEach(assignment => {
    const count = assignment.length;
    if (count <= categoryCount) {
      multiCategoryCount[count]++;
    }
  });

  // Create word frequencies for word cloud
  const wordFrequencies = Object.entries(wordFreq)
    .map(([word, freq]) => ({ name: word, value: freq }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 50);

  // Create category mapping (simplified display names)
  const categoryMapping: Record<string, string> = {};
  categories.forEach(cat => {
    categoryMapping[cat] = cat; // Use the name as-is since it's already human-readable
  });

  return {
    categories,
    categoryCounts,
    wordFrequencies,
    multiCategoryCount,
    coOccurrenceMatrix,
    coOccurrenceData,
    categoryMapping
  };
}

function processLocationStats(records: JSONRecord[]): LocationStats {
  const withLocation = records.filter(r => {
    const loc = r.classification.location;
    // Use 'present' field as the authoritative indicator for location presence
    // When present is 'N', the keyword does NOT have a location regardless of other fields
    return loc && loc.present === 'Y';
  });

  const cityDistribution: Record<string, number> = {};
  const stateDistribution: Record<string, number> = {};
  let totalConfidence = 0;
  let confidenceCount = 0;

  withLocation.forEach(record => {
    const loc = record.classification.location;
    
    // Handle both frontend expected fields and backend actual fields
    const city = loc.city_or_town || (typeof loc.detected_location === 'string' && loc.detected_location.includes(',') ? loc.detected_location.split(',')[0].trim() : null);
    const state = loc.state_or_province;
    
    if (city) {
      cityDistribution[city] = (cityDistribution[city] || 0) + 1;
    }
    
    if (state) {
      stateDistribution[state] = (stateDistribution[state] || 0) + 1;
    }
    
    // Handle confidence - backend uses 'location_confidence'
    const confidence = loc.location_confidence ?? loc.confidence;
    if (typeof confidence === 'number') {
      totalConfidence += confidence;
      confidenceCount++;
    }
  });

  return {
    totalWithLocation: withLocation.length,
    percentageWithLocation: records.length > 0 ? (withLocation.length / records.length) * 100 : 0,
    averageConfidence: confidenceCount > 0 ? totalConfidence / confidenceCount : 0,
    cityDistribution,
    stateDistribution
  };
}

function processIntentStats(records: JSONRecord[]): IntentStats {
  let totalCommercial = 0;
  let totalSpecificity = 0;
  let commercialCount = 0;
  let specificityCount = 0;
  const intentDistribution: Record<string, number> = {};

  records.forEach(record => {
    const scores = record.classification.scores || {};
    const metadata = record.classification.metadata || {};
    
    // Handle both expected field names and variations
    const commercialScore = scores.intent_commercial_score ?? scores.commercial_score;
    const specificityScore = scores.intent_specificity_score ?? scores.specificity_score;
    
    if (typeof commercialScore === 'number') {
      totalCommercial += commercialScore;
      commercialCount++;
    }
    
    if (typeof specificityScore === 'number') {
      totalSpecificity += specificityScore;
      specificityCount++;
    }
    
    // Handle search intent - might be in metadata or scores
    const searchIntent = metadata.search_intent || metadata.intent || scores.search_intent;
    if (searchIntent) {
      intentDistribution[searchIntent] = (intentDistribution[searchIntent] || 0) + 1;
    }
  });

  return {
    averageCommercialScore: commercialCount > 0 ? totalCommercial / commercialCount : 0,
    averageSpecificityScore: specificityCount > 0 ? totalSpecificity / specificityCount : 0,
    intentDistribution
  };
}

function processSourceStats(records: JSONRecord[]): SourceStats {
  const topicSourceDistribution: Record<string, number> = {};
  const subtopicSourceDistribution: Record<string, number> = {};

  records.forEach(record => {
    record.classification.topics.forEach(topic => {
      if (topic.source_module) {
        topicSourceDistribution[topic.source_module] = (topicSourceDistribution[topic.source_module] || 0) + 1;
      }
    });
    
    record.classification.subtopics.forEach(subtopic => {
      if (subtopic.source_module) {
        subtopicSourceDistribution[subtopic.source_module] = (subtopicSourceDistribution[subtopic.source_module] || 0) + 1;
      }
    });
  });

  return {
    topicSourceDistribution,
    subtopicSourceDistribution
  };
}

// Export function to convert JSON data to CSV for downloads
export function convertJSONToCSV(records: JSONRecord[]): string {
  const headers = [
    'ID',
    'Query',
    'Topic',
    'Subtopic',
    'Categories',
    'Location_City',
    'Location_State',
    'Location_Country',
    'Location_Confidence',
    'Commercial_Score',
    'Specificity_Score',
    'Search_Intent',
    'Is_Valid',
    'Source_Module'
  ];

  const rows = records.map(record => {
    const topics = record.classification.topics.map(t => t.name).join('; ');
    const subtopics = record.classification.subtopics.map(s => s.name).join('; ');
    const loc = record.classification.location || {};
    const scores = record.classification.scores || {};
    const metadata = record.classification.metadata || {};
    
    // Get primary source module
    const sourceModules = new Set<string>();
    record.classification.topics.forEach(t => {
      if (t.source_module) sourceModules.add(t.source_module);
    });
    record.classification.subtopics.forEach(s => {
      if (s.source_module) sourceModules.add(s.source_module);
    });
    const sourceModule = Array.from(sourceModules).join('; ');

    return [
      record.id,
      `"${record.query}"`,
      `"${topics}"`,
      `"${subtopics}"`,
      '""', // Categories field - will be populated if using categorical classification
      loc.city_or_town || '',
      loc.state_or_province || '',
      loc.country || '',
      loc.location_confidence || '',
      scores.intent_commercial_score || '',
      scores.intent_specificity_score || '',
      metadata.search_intent || '',
      record.validation.is_valid ? 'Yes' : 'No',
      sourceModule
    ];
  });

  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}

// Chart exporting options helper
export function getExportingOptions(title: string) {
  return {
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadCSV']
        }
      },
      filename: `keyword-classification-${title.toLowerCase().replace(/\s+/g, '-')}`
    },
    credits: {
      enabled: false
    }
  };
} 